// @flow
import type { EventRecordI } from "./EventRecord";
import type { EventHost } from "./EventRecord";
import EventRecord from "./EventRecord";
import type { EventRegistrantRecordI } from "./RegistrantRecord";
import RegistrantRecord from "./RegistrantRecord";
import type { ReviewActionRecordI } from "./ReviewActionRecord";
import ReviewActionRecord from "./ReviewActionRecord";

type BizhubAddress = {
  address1?: ?string,
  address2?: ?string,
  city?: ?string,
  state?: ?string,
  postalCode?: ?string,
  venueName?: ?string,
};

type BizhubEventMeta = {
  hasLiveFire?: boolean,
};

type BizhubEventAttributes = {
  name?: string,
  url?: string,
  status?: string,
  reviewStatus?: ?string,
  createdBy?: string,
  price?: number,
  registrantCount?: number,
  capacity?: number,
  timezone?: string,
  address?: ?BizhubAddress,
  meta?: ?BizhubEventMeta,
  hostAttendeeId?: ?string,
  hosts?: Array<EventHost>,
  startDate?: string,
  endDate?: string,
};

export type BizhubEvent = {
  id: string,
  createdBy?: string,
  attributes: BizhubEventAttributes,
};

export type ParsedEvents = {
  events: {
    [string]: EventRecordI,
  },
  eventIds: Array<string>,
};

export type EventReviewAction = {
  id: string,
  attributes?: {
    eventId?: string,
    actionType?: string,
    actorUserId?: string,
    actionNote?: ?string,
    createdAt?: string,
  },
};

type EventRegistrantAttributes = {
  firstName?: string,
  lastName?: string,
  phone?: string,
  displayPhone?: string,
  email?: string,
  checkedIn?: boolean,
  isCheckedIn?: boolean,
  checkedInAt?: ?string,
  status?: string,
};

export type EventRegistrant = {
  id: string,
  attributes?: EventRegistrantAttributes,
};

export type ParsedRegistrants = {
  registrants: {
    [string]: EventRegistrantRecordI,
  },
  registrantIds: Array<string>,
};

const CHECKED_IN_STATUSES = ["attended", "checked-in", "checked_in"];

const dedupeHosts = (hosts: Array<EventHost> = []): Array<EventHost> => {
  const seen: { [string]: boolean } = {};

  return hosts.filter((host: EventHost): boolean => {
    const id = host.id || host.userId || `${host.firstName} ${host.lastName}`;

    if (seen[id]) {
      return false;
    }

    seen[id] = true;
    return true;
  });
};

export const parseEventReturnToRecord = (event: BizhubEvent): EventRecordI => {
  const { id, attributes = {} } = event;
  const address = attributes.address || {}; // API sends null, not undefined

  return new EventRecord({
    id,
    name: attributes.name || "",
    url: attributes.url || "",
    status: attributes.status || "",
    reviewStatus: attributes.reviewStatus || "",
    createdBy: attributes.createdBy || event.createdBy || "",
    price: attributes.price || 0,
    registrantCount: attributes.registrantCount || 0,
    capacity: attributes.capacity || 0,
    timezone: attributes.timezone || "",
    venueName: address.venueName || "",
    address1: address.address1 || "",
    city: address.city || "",
    state: address.state || "",
    hasLiveFire: !!(attributes.meta && attributes.meta.hasLiveFire),
    hostAttendeeId: attributes.hostAttendeeId || null,
    hosts: dedupeHosts(attributes.hosts),
    startDate: attributes.startDate || "",
    endDate: attributes.endDate || "",
  });
};

export const parseEvents = (data: Array<BizhubEvent> = []): ParsedEvents => {
  const events: { [string]: EventRecordI } = {};
  const eventIds = [];

  data.forEach((event: BizhubEvent) => {
    const record = parseEventReturnToRecord(event);
    events[event.id] = record;
    eventIds.push(event.id);
  });

  return { events, eventIds };
};

export const parseReviewActions = (
  data: Array<EventReviewAction> = []
): Array<ReviewActionRecordI> =>
  data.map(
    ({ id, attributes = {} }: EventReviewAction): ReviewActionRecordI =>
      new ReviewActionRecord({
        id,
        eventId: attributes.eventId || "",
        actionType: attributes.actionType || "",
        actorUserId: attributes.actorUserId || "",
        actionNote: attributes.actionNote || null,
        createdAt: attributes.createdAt || "",
      })
  );

export const parseRegistrantReturnToRecord = (
  registrant: EventRegistrant
): EventRegistrantRecordI => {
  const { id, attributes = {} } = registrant;
  const checkedIn =
    !!attributes.checkedIn ||
    !!attributes.isCheckedIn ||
    !!attributes.checkedInAt ||
    CHECKED_IN_STATUSES.includes((attributes.status || "").toLowerCase());

  return new RegistrantRecord({
    id,
    firstName: attributes.firstName || "",
    lastName: attributes.lastName || "",
    phone: attributes.displayPhone || attributes.phone || "",
    email: attributes.email || "",
    status: attributes.status || "",
    checkedIn,
  });
};

export const parseRegistrants = (
  data: Array<EventRegistrant> = []
): ParsedRegistrants => {
  const registrants: { [string]: EventRegistrantRecordI } = {};
  const registrantIds = [];

  data.forEach((registrant: EventRegistrant) => {
    const record = parseRegistrantReturnToRecord(registrant);
    registrants[registrant.id] = record;
    registrantIds.push(registrant.id);
  });

  return { registrants, registrantIds };
};
